import { EyeIcon } from '@heroicons/react/24/outline';

import { IconButton } from '@/components/IconButton';
import { SeverityIndicator } from '@/components/SeverityIndicator';
import { getColumnWidth } from '@/components/table/columns/registry';
import { ColumnPriority, Columns } from '@/components/table/types';
import { StatusChip } from '@/components/ui/status-chip';
import { LastCompromiseChip } from '@/sections/customerManagement/components/badges/LastCompromiseChip';
import {
  CustomerTypeTag,
  StatusBadge,
} from '@/sections/customerManagement/components/badges/StatusBadge';
import { CustomerNameWithLogo } from '@/sections/customerManagement/components/CustomerNameWithLogo';
import {
  DEFAULT_COLUMN_ORDER,
  HIDDEN_COLUMNS,
  isColumnSortable,
} from '@/sections/customerManagement/config/columns';
import { CustomerData, Filters } from '@/sections/customerManagement/types';
import { RiskSeverity } from '@/types';
import { formatDate } from '@/utils/date.util';
import { deletionStatusVariants } from '@/utils/statusChip.util';

export const getColumns = ({
  onViewAccount,
  isPraetorianUser = true,
}: {
  onViewAccount?: (customer: CustomerData) => void;
  isPraetorianUser?: boolean;
}): Columns<CustomerData> => {
  const columns: Columns<CustomerData> = [
    {
      label: 'View',
      id: '',
      cell: (customer: CustomerData) => (
        <IconButton
          icon={<EyeIcon className="size-5" />}
          onClick={e => {
            // Prevent row click handler from opening drawer when clicking View button
            e.stopPropagation();
            onViewAccount?.(customer);
          }}
          aria-label="View account"
          variant="primary"
          size="sm"
        />
      ),
      ...getColumnWidth('customer', 'view'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: 'Status',
      id: 'status',
      isLoading: (customer: CustomerData) =>
        customer?.statusStatus === 'pending',
      cell: (customer: CustomerData) => (
        <StatusBadge status={customer.status} />
      ),
      sorting: isColumnSortable('status'),
      ...getColumnWidth('customer', 'status'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: isPraetorianUser ? 'Customer' : 'Tenant',
      id: 'displayName',
      cell: (customer: CustomerData) => (
        <CustomerNameWithLogo customer={customer} />
      ),
      sorting: isColumnSortable('displayName'),
      ...getColumnWidth('customer', 'displayName'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
  ];

  // Only show the Type column for Praetorian users
  if (isPraetorianUser) {
    columns.push({
      label: 'Type',
      id: 'type',
      isLoading: (customer: CustomerData) => customer?.typeStatus === 'pending',
      cell: (customer: CustomerData) => (
        <CustomerTypeTag type={customer.type} />
      ),
      sorting: isColumnSortable('type'),
      ...getColumnWidth('customer', 'type'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    });
  }

  // Only show Last Compromise column for Praetorian users
  if (isPraetorianUser) {
    columns.push({
      label: 'Last Compromise',
      id: 'lastCompromiseDate',
      isLoading: (customer: CustomerData) =>
        customer?.lastCompromiseStatus === 'pending',
      cell: (customer: CustomerData) => (
        <LastCompromiseChip
          lastCompromiseDate={customer.lastCompromiseDate}
          status={customer.status}
          type={customer.type}
          subscriptionEndDate={customer.subscriptionEndDate}
        />
      ),
      sorting: isColumnSortable('lastCompromiseDate'),
      ...getColumnWidth('customer', 'lastCompromiseDate'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    });
  }

  // Deletion status column — Praetorian-only (the whole deletion surface is
  // gated on isPraetorianUser; backend C1 SuperAdmin 403 remains authoritative).
  // Empty cell when the tenant has no deletion record.
  if (isPraetorianUser) {
    columns.push({
      label: 'Deletion',
      id: 'deletion',
      isLoading: (customer: CustomerData) =>
        customer?.deletionStatus === 'pending',
      cell: (customer: CustomerData) =>
        customer.deletion ? (
          <StatusChip
            label={customer.deletion.state}
            variant={deletionStatusVariants[customer.deletion.state]}
            tooltip={
              customer.deletion.scheduledPurgeAt
                ? `Scheduled purge: ${formatDate(customer.deletion.scheduledPurgeAt)}`
                : undefined
            }
          />
        ) : null,
      sorting: isColumnSortable('deletion'),
      ...getColumnWidth('customer', 'deletion'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    });
  }

  columns.push(
    {
      label: 'Critical',
      id: 'critical',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.Critical}
          count={customer.critical || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-critical-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('critical'),
      ...getColumnWidth('customer', 'critical'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      label: 'High',
      id: 'high',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.High}
          count={customer.high || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-high-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('high'),
      ...getColumnWidth('customer', 'high'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      label: 'Medium',
      id: 'medium',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.Medium}
          count={customer.medium || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-medium-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('medium'),
      ...getColumnWidth('customer', 'medium'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      label: 'Low',
      id: 'low',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.Low}
          count={customer.low || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-low-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('low'),
      ...getColumnWidth('customer', 'low'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      label: 'Info',
      id: 'info',
      isLoading: (customer: CustomerData) =>
        customer?.riskCountsStatus === 'pending',
      cell: (customer: CustomerData) => (
        <SeverityIndicator
          severity={RiskSeverity.Info}
          count={customer.info || 0}
          size="md"
          showZero
          animate
          data-testid={`severity-info-${customer.email}`}
        />
      ),
      sorting: isColumnSortable('info'),
      ...getColumnWidth('customer', 'info'),
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    }
  );

  return columns;
};

export const defaultFilters: Filters = {
  search: '',
  advancedFilters: {},
};

export const getSortableColumnsConfig = ({
  defaultColumns,
}: {
  defaultColumns: Columns<CustomerData>;
}) => {
  // Use default column order from configuration
  const defaultOrder = [...DEFAULT_COLUMN_ORDER];

  // Find the view column (action column with empty string ID)
  const viewColumn = defaultColumns.find(col => col.label === 'View');

  // Build active columns with View at the beginning
  const activeColumns = defaultOrder
    .map(id => {
      const column = defaultColumns.find(col => col.id === id);
      return column
        ? {
            label: column.label,
            id: column.id,
            disabled: id === 'displayName',
            isDefault: id === 'displayName',
          }
        : null;
    })
    .filter(item => item !== null);

  // Prepend the view column to active columns
  if (viewColumn) {
    activeColumns.unshift({
      label: viewColumn.label,
      id: viewColumn.id,
      disabled: true,
      isDefault: true,
    });
  }

  // Build available (hidden-by-default but re-addable) columns from HIDDEN_COLUMNS
  const availableColumns = [...HIDDEN_COLUMNS]
    .map(id => {
      const column = defaultColumns.find(col => col.id === id);
      return column ? { label: column.label, id: column.id } : null;
    })
    .filter(item => item !== null);

  return {
    fixed: [],
    active: activeColumns,
    available: availableColumns,
  };
};
